import { ProgramType } from 'types';

import { api } from './api';

interface GetWarrantyProviderResponse {
  data: {
    programType: ProgramType;
    providers: {
      name: string;
      default: boolean;
    }[];
  }[];
}

interface GetWarrantyProviderQuery {
  programType?: ProgramType;
}

interface WarrantyContractSkuResponse {
  id: string;
  warrantyProvider: string;
  programType: string;
  term: number;
  contractSku: string;
  payoutAmount?: number;
  displayName?: string;
  description?: string;
  status: string;
  createdAt: string;
  discontinuedAt?: string;
}

export const warrantiesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWarrantyProviders: build.query<GetWarrantyProviderResponse, GetWarrantyProviderQuery>({
      query: (queryParams) => ({
        url: `/warranties/providers${
          queryParams &&
          '?' +
            new URLSearchParams({
              ...queryParams,
            }).toString()
        }`,
      }),
      providesTags: () => [{ type: 'WarrantyProviders', id: 'LIST' }],
    }),

    getWarrantyContractSkus: build.query<
      WarrantyContractSkuResponse[],
      { warrantyProvider?: string; programType?: string; term?: number }
    >({
      query: (params = {}) => {
        const search = new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)]),
          ),
        );
        const qs = search.toString();
        return { url: `/admin/warranty-contract-skus${qs ? `?${qs}` : ''}` };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'WarrantyContractSkus' as const, id })),
              { type: 'WarrantyContractSkus' as const, id: 'LIST' },
            ]
          : [{ type: 'WarrantyContractSkus' as const, id: 'LIST' }],
    }),

    createWarrantyContractSku: build.mutation<
      WarrantyContractSkuResponse,
      {
        warrantyProvider: string;
        programType: string;
        term: number;
        contractSku: string;
        payoutAmount?: number;
        displayName?: string;
        description?: string;
      }
    >({
      query: (body) => ({ url: '/admin/warranty-contract-skus', method: 'POST', body }),
      invalidatesTags: [{ type: 'WarrantyContractSkus', id: 'LIST' }],
    }),

    setWarrantyContractSkuStatus: build.mutation<WarrantyContractSkuResponse, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/admin/warranty-contract-skus/${id}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'WarrantyContractSkus' as const, id },
        { type: 'WarrantyContractSkus' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetWarrantyProvidersQuery,
  useGetWarrantyContractSkusQuery,
  useCreateWarrantyContractSkuMutation,
  useSetWarrantyContractSkuStatusMutation,
} = warrantiesApi;
